import {useEffect, useMemo} from 'react';

import {ChipKind, daysUntil, expiryLabel, initials, sourceLabel, tabKindOf} from '../lib/aggregate';
import {kindHeading} from '../lib/chip-style';
import {GrantView} from '../lib/grant-view';

interface Props {
    open: boolean;
    resource: string | null;
    /** Source kind that triggered the drawer; the drawer still shows both kinds. */
    kind: ChipKind | 'both' | null;
    /** All grants (any user, any kind) — drawer filters down to the resource. */
    allGrants: GrantView[];
    onClose: () => void;
}

function formatExpiry(iso: string | undefined): string {
    if (!iso) {
        return 'Permanent';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: '2-digit'});
}

function timeLeftLabel(iso: string | undefined): string {
    if (!iso) {
        return '∞';
    }
    return expiryLabel(daysUntil(iso));
}

interface UserBlock {
    email: string;
    name: string;
    dataAccess: GrantView[];
    impersonation: GrantView[];
}

/**
 * Side drawer that lists everyone with access to a specific tenant — opened by
 * clicking a tenant chip in any row. The chip's kind seeds the drawer header,
 * but the body always shows both data-access and impersonation so an admin can
 * see the whole picture for that tenant without switching tabs.
 */
export function TenantDrawer({open, resource, kind, allGrants, onClose}: Props) {
    useEffect(() => {
        if (!open) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const blocks: UserBlock[] = useMemo(() => {
        if (!resource) {
            return [];
        }
        const byUser = new Map<string, UserBlock>();
        for (const g of allGrants) {
            if (g.resource !== resource) {
                continue;
            }
            const chipKind = tabKindOf(g.kind);
            if (!chipKind) {
                continue;
            }
            let b = byUser.get(g.email);
            if (!b) {
                b = {email: g.email, name: nameFromEmail(g.email), dataAccess: [], impersonation: []};
                byUser.set(g.email, b);
            }
            if (chipKind === 'data-access') {
                b.dataAccess.push(g);
            } else {
                b.impersonation.push(g);
            }
        }
        return Array.from(byUser.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [resource, allGrants]);

    const tenantName = useMemo(() => {
        if (!resource) {
            return null;
        }
        const g = allGrants.find(x => x.resource === resource && x.resourceName);
        return g?.resourceName ?? null;
    }, [resource, allGrants]);

    const isWildcard = resource === '*';
    const isUnknown = resource === '?';
    const headerTitle = isWildcard
        ? 'All tenants (wildcard)'
        : isUnknown
        ? 'Unknown tenant'
        : tenantName ?? resource ?? '';
    const headerSub = isWildcard || isUnknown ? null : tenantName ? resource : null;
    const totalUsers = blocks.length;
    const totalGrants = blocks.reduce((s, b) => s + b.dataAccess.length + b.impersonation.length, 0);

    return (
        <>
            <div className={`drawer-backdrop${open ? ' open' : ''}`} onClick={onClose} aria-hidden />
            <aside className={`drawer${open ? ' open' : ''}`} role="dialog" aria-modal aria-hidden={!open}>
                {resource && (
                    <>
                        <div className="drawer-head">
                            <span
                                className={`avatar lg${isWildcard ? ' avatar-wildcard' : ''}`}
                                aria-hidden
                            >
                                {isWildcard ? '∗' : isUnknown ? '?' : initials(tenantName ?? '', resource)}
                            </span>
                            <div style={{flex: 1, minWidth: 0}}>
                                <div className="title">{headerTitle}</div>
                                <div className="sub">
                                    {headerSub ? `${headerSub} · ` : ''}
                                    {totalUsers} {totalUsers === 1 ? 'user' : 'users'} · {totalGrants}{' '}
                                    {totalGrants === 1 ? 'grant' : 'grants'}
                                    {kind && kind !== 'both' ? ` · opened from ${kindHeading(kind)}` : ''}
                                </div>
                            </div>
                            <button className="btn-icon" onClick={onClose} type="button" aria-label="Close">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>

                        <div className="drawer-body">
                            {blocks.length === 0 ? (
                                <div className="empty-opt" style={{padding: '24px 0'}}>
                                    No grants for this tenant.
                                </div>
                            ) : (
                                blocks.map(b => (
                                    <section className="drawer-section" key={b.email}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <span className="avatar" aria-hidden>
                                                {initials(b.name, b.email)}
                                            </span>
                                            <div style={{minWidth: 0}}>
                                                <div style={{fontWeight: 600, fontSize: 13}}>{b.name || b.email}</div>
                                                <div style={{color: 'var(--en-fg-secondary)', fontSize: 12}}>
                                                    {b.email}
                                                </div>
                                            </div>
                                        </div>
                                        {[...b.dataAccess, ...b.impersonation].map(g => {
                                            const k = tabKindOf(g.kind);
                                            return (
                                                <div className="drawer-row" key={g.id}>
                                                    <div style={{minWidth: 0, flex: 1}}>
                                                        <div className="res">
                                                            {k ? kindHeading(k) : 'Unknown'}
                                                        </div>
                                                        <div className="meta">
                                                            <span className={`src-dot src-${g.source}`} />
                                                            <span>{sourceLabel(g.source)}</span>
                                                            <span>·</span>
                                                            <span>{formatExpiry(g.expiresAt)}</span>
                                                        </div>
                                                    </div>
                                                    <span className="when">{timeLeftLabel(g.expiresAt)}</span>
                                                </div>
                                            );
                                        })}
                                    </section>
                                ))
                            )}
                        </div>
                    </>
                )}
            </aside>
        </>
    );
}

function nameFromEmail(email: string): string {
    const local = email.split('@')[0] ?? email;
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map(p => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
}
